// ChecklistForm.ts
import { loadRecaptchaScript, loadRecaptchaOnInteraction } from '../utils/recaptcha';
import { trackChecklistDownload } from '../utils/analytics';
import { getAttributionForForms } from '../utils/attribution';

// Déclaration pour éviter les erreurs TypeScript avec gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

class ChecklistForm {
  private form!: HTMLFormElement;
  private submitButton!: HTMLButtonElement;
  private isSubmitting: boolean = false;
  private apiUrl: string = '';
  private static instance: ChecklistForm | null = null;
  
  private getCurrentLang(): 'fr' | 'en' {
    const lang = (localStorage.getItem('cryptoaccounting-lang') || 'fr').toLowerCase();
    return lang === 'en' ? 'en' : 'fr';
  }

  private trLocal(key: string): string {
    const lang = this.getCurrentLang();
    const dict: Record<string, { fr: string; en: string }> = {
      'errors.required': { fr: 'Ce champ est requis', en: 'This field is required' },
      'errors.email': { fr: 'Veuillez entrer une adresse email valide', en: 'Please enter a valid email address' },
      'errors.min2': { fr: 'Ce champ doit contenir au moins 2 caractères', en: 'This field must contain at least 2 characters' },
      'checklist.form.loading': { fr: 'Traitement en cours...', en: 'Processing...' },
      'checklist.form.submit': { fr: 'Télécharger ma checklist', en: 'Download my checklist' },
      'checklist.form.fix_errors': { fr: 'Veuillez corriger les erreurs dans le formulaire', en: 'Please correct the errors in the form' },
      'checklist.form.submit_success': { fr: 'Votre checklist a été envoyée avec succès! Vérifiez votre boîte de réception dans quelques instants.', en: 'Your checklist has been sent successfully! Check your inbox shortly.' },
      'checklist.form.config_error': { fr: "Configuration incomplète. Contactez l'administrateur.", en: 'Incomplete configuration. Please contact the administrator.' },
      'checklist.form.generic_error': { fr: 'Une erreur est survenue. Veuillez réessayer.', en: 'An error occurred. Please try again.' },
    };
    return dict[key]?.[lang] || key;
  }

  constructor() {
    // Pattern Singleton pour éviter les doubles initialisations
    if (ChecklistForm.instance) {
      console.log("ChecklistForm déjà initialisé");
      return ChecklistForm.instance;
    }

    this.form = document.getElementById('checklistForm') as HTMLFormElement;
    if (!this.form) {
      console.error("Formulaire de checklist non trouvé dans le DOM");
      return;
    }

    this.submitButton = this.form.querySelector('button[type="submit"]') as HTMLButtonElement;
    
    // Utilisation de l'URL de l'API définie dans l'environnement
    this.apiUrl = import.meta.env.VITE_API_URL || '';
    
    if (!this.apiUrl) {
      console.error("⚠️ ERREUR: URL de l'API non définie dans les variables d'environnement");
    } else {
      console.log("✓ URL de l'API trouvée pour le formulaire de checklist:", this.apiUrl);
    }

    this.initialize();

    // Tester la disponibilité de Google Analytics
    setTimeout(() => this.testAnalyticsAvailability(), 2000);

    ChecklistForm.instance = this;
  }

  private async initialize(): Promise<void> {
    console.log("Initialisation du formulaire de checklist");

    // reCAPTCHA chargé à la première interaction avec le formulaire, pas à l'ouverture de la page
    loadRecaptchaOnInteraction(this.form);

    // Gestion de la soumission du formulaire
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // Validation des champs
      const inputs = this.form.querySelectorAll('input');
      let isFormValid = true;

      inputs.forEach(input => {
        if (!this.validateField(input as HTMLInputElement)) {
          isFormValid = false;
        }
      });

      if (!isFormValid) {
        this.showToastKey('checklist.form.fix_errors', 'error');
        return;
      }

      // Afficher le modal reCAPTCHA
      await this.showRecaptchaModal();
    });

    // Validation en temps réel
    const requiredInputs = this.form.querySelectorAll('input[required]');
    requiredInputs.forEach(input => {
      input.addEventListener('input', () => this.validateField(input as HTMLInputElement));
      input.addEventListener('blur', () => this.validateField(input as HTMLInputElement));
    });

    // Tracking des interactions avec le formulaire
    const formFields = this.form.querySelectorAll('input, select, textarea');
    formFields.forEach(field => {
      field.addEventListener('focus', () => {
        if (window.gtag) {
          window.gtag('event', 'form_start', {
            'event_category': 'engagement',
            'event_label': 'Début remplissage checklist',
            'form_id': 'checklistForm'
          });
        }
      }, { once: true }); // Ne déclencher qu'une seule fois
    });
  }

  private testAnalyticsAvailability(): void {
    console.log("🔍 Vérification de Google Analytics...");

    if (window.gtag) {
      console.log("✅ Google Analytics est disponible");

      // Envoi d'un événement de test
      if (import.meta.env.DEV) { // Uniquement en développement
        window.gtag('event', 'test', {
          'event_category': 'debug',
          'event_label': 'Checklist Form Test',
          'debug_mode': true
        });
        console.log("🧪 Événement de test envoyé à GA");
      }
    } else {
      console.warn("⚠️ Google Analytics n'est pas disponible");
      console.log("📋 Vérifiez que:");
      console.log("   1. Le script GA est bien chargé dans la page");
      console.log("   2. L'initialisation de gtag est effectuée correctement");
      console.log("   3. Il n'y a pas de bloqueur de publicité activé");
    }
  }

  private async showRecaptchaModal(): Promise<void> {
    // Créer l'overlay du modal
    const overlay = document.createElement('div');
    overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';

    // Créer le contenu du modal
    const modal = document.createElement('div');
    modal.className = 'bg-white rounded-xl p-6 shadow-xl max-w-md w-full mx-4 relative';

    // En-tête du modal (i18n)
    const header = document.createElement('div');
    header.className = 'mb-4 text-center';
    header.innerHTML = `
      <h3 class="text-xl font-semibold text-[#060515]" data-translate="checklist.modal.title">Vérification de sécurité</h3>
      <p class="text-gray-600 mt-2" data-translate="checklist.modal.subtitle">Pour télécharger votre checklist, merci de confirmer que vous n'êtes pas un robot.</p>
    `;

    // Container pour le reCAPTCHA
    const recaptchaContainer = document.createElement('div');
    recaptchaContainer.className = 'flex justify-center my-4';

    // Widget reCAPTCHA - Utiliser un ID unique pour le conteneur
    const recaptchaWidget = document.createElement('div');
    recaptchaWidget.id = 'checklist-recaptcha-widget';
    recaptchaWidget.className = 'g-recaptcha';

    // Message d'erreur pour reCAPTCHA
    const errorDiv = document.createElement('div');
    errorDiv.id = 'recaptcha-checklist-error';
    errorDiv.className = 'hidden text-red-500 text-sm text-center mt-2';
    errorDiv.setAttribute('data-translate', 'checklist.modal.captcha_prompt');
    errorDiv.textContent = 'Veuillez confirmer que vous n\'êtes pas un robot';

    // Bouton de soumission
    const submitButton = document.createElement('button');
    submitButton.className = 'btn-primary w-full mt-4';
    submitButton.setAttribute('data-translate', 'checklist.modal.submit');
    submitButton.textContent = 'Valider et recevoir la checklist';

    // Bouton de fermeture
    const closeButton = document.createElement('button');
    closeButton.className = 'absolute top-3 right-3 text-gray-400 hover:text-gray-600';
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    `;

    // Assembler le modal
    recaptchaContainer.appendChild(recaptchaWidget);
    modal.appendChild(closeButton);
    modal.appendChild(header);
    modal.appendChild(recaptchaContainer);
    modal.appendChild(errorDiv);
    modal.appendChild(submitButton);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // IMPORTANT: Rendre le reCAPTCHA après avoir ajouté le widget au DOM
    try {
      // Le script peut être encore en cours de chargement (chargé à la première interaction)
      const scriptLoaded = await loadRecaptchaScript();
      if (!scriptLoaded) {
        errorDiv.setAttribute('data-translate', 'checklist.modal.load_error');
        errorDiv.textContent = "Erreur: Impossible de charger reCAPTCHA. Veuillez rafraîchir la page.";
        errorDiv.classList.remove('hidden');
        return;
      }

      console.log("Rendu du widget reCAPTCHA avec la clé site:", import.meta.env.VITE_RECAPTCHA_SITE_KEY);
      window.grecaptcha.render('checklist-recaptcha-widget', {
        'sitekey': import.meta.env.VITE_RECAPTCHA_SITE_KEY,
        'callback': function(response: string) {
          // Cacher le message d'erreur quand reCAPTCHA est validé
          if (response) {
            errorDiv.classList.add('hidden');
          }
        }
      });
      console.log("Widget reCAPTCHA rendu avec succès");
    } catch (error) {
      console.error("Erreur lors du rendu du reCAPTCHA pour la checklist:", error);
      errorDiv.setAttribute('data-translate', 'checklist.modal.render_error');
      errorDiv.textContent = "Erreur: Impossible d'afficher le captcha. Veuillez rafraîchir la page.";
      errorDiv.classList.remove('hidden');
    }
    // Appliquer la traduction au contenu du modal selon la langue courante
    try {
      const currentLang = localStorage.getItem('cryptoaccounting-lang') || 'fr';
      (window as any).switchLang?.(currentLang);
    } catch {}
    // Gérer les événements
    return new Promise((resolve) => {
      closeButton.addEventListener('click', () => {
        document.body.removeChild(overlay);
        resolve();
      });

      submitButton.addEventListener('click', async () => {
        try {
          // @ts-ignore
          const response = grecaptcha.getResponse();

          if (!response || response.length === 0) {
            errorDiv.classList.remove('hidden');
            return;
          }

          // Cacher le message d'erreur si reCAPTCHA est valide
          errorDiv.classList.add('hidden');

          // Fermer le modal
          document.body.removeChild(overlay);

          // Soumettre le formulaire
          this.handleSubmit(response);
        } catch (error) {
          console.error("❌ Erreur lors de la vérification du reCAPTCHA pour la checklist:", error);
          errorDiv.classList.remove('hidden');
        }
      });
    });
  }

  private validateField(field: HTMLInputElement): boolean {
    const value = field.value.trim();
    let isValid = true;
    let errorMessage = '';

    field.classList.remove('border-red-500', 'border-green-500');
    this.removeErrorMessage(field);

    if (field.required && !value) {
      isValid = false;
      errorMessage = 'Ce champ est requis';
    } else {
      switch (field.id) {
        case 'checklistEmail':
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            isValid = false;
            errorMessage = 'Veuillez entrer une adresse email valide';
          }
          break;

        case 'checklistFirstName':
        case 'checklistLastName':
          if (value.length < 2) {
            isValid = false;
            errorMessage = 'Ce champ doit contenir au moins 2 caractères';
          }
          break;

        case 'checklistCompany':
          if (value.length < 2) {
            isValid = false;
            errorMessage = 'Ce champ doit contenir au moins 2 caractères';
          }
          break;
      }
    }

    if (field.required || value) {
      field.classList.add(isValid ? 'border-green-500' : 'border-red-500');
      if (!isValid) {
        this.showErrorMessage(field, errorMessage);
      }
    }

    return isValid;
  }

  private async handleSubmit(recaptchaToken: string): Promise<void> {
    if (this.isSubmitting) return;

    console.log("⚙️ Soumission du formulaire de checklist...");

    if (!this.apiUrl) {
      this.showToastKey('checklist.form.config_error', 'error');
      return;
    }

    const formData = new FormData(this.form);
    const email = formData.get("email") as string;
    const firstName = formData.get("firstName") as string || '';
    const lastName = formData.get("lastName") as string || '';
    const company = formData.get("company") as string || '';
    const optIn = formData.get("optIn") ? true : false;

    try {
      this.isSubmitting = true;
      this.updateSubmitButton(true);

      // Préparer les données pour l'API
      const requestData = {
        email,
        firstName,
        lastName,
        company,
        optIn,
        recaptchaToken,
        formType: 'checklist',
        source: window.location.href,
        ...getAttributionForForms()
      };

      console.log("📤 Envoi des données au serveur backend...", requestData);

      // Appel à l'API backend
      const response = await fetch(`${this.apiUrl}/api/forms/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Erreur lors de l'envoi du formulaire");
      }

      console.log("✅ Réponse du serveur:", result);

      // Réinitialiser le formulaire en cas de succès
      this.form.reset();
      const inputs = this.form.querySelectorAll('input');
      inputs.forEach(input => {
        input.classList.remove('border-green-500', 'border-red-500');
      });

      this.showToastKey('checklist.form.submit_success', 'success');

      // Tracking Google Analytics
      try {
        trackChecklistDownload(`Téléchargement checklist crypto depuis ${window.location.pathname}`);
        console.log('✓ Événement de téléchargement de checklist tracké via analytics.ts');
      } catch (analyticsError) {
        // Ne pas bloquer l'expérience utilisateur si le tracking échoue
        console.warn('⚠️ Erreur de tracking:', analyticsError);
      }

    } catch (error) {
      console.error("❌ Erreur:", error);
      if (error instanceof Error && error.message) {
        this.showToast(error.message, 'error');
      } else {
        this.showToastKey('checklist.form.generic_error', 'error');
      }
    } finally {
      this.isSubmitting = false;
      this.updateSubmitButton(false);
    }
  }

  private updateSubmitButton(isLoading: boolean): void {
    this.submitButton.disabled = isLoading;
    if (isLoading) {
      this.submitButton.innerHTML = '<svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span data-translate="checklist.form.loading">' + this.trLocal('checklist.form.loading') + '</span>';
    } else {
      this.submitButton.setAttribute('data-translate', 'checklist.form.submit');
      this.submitButton.textContent = this.trLocal('checklist.form.submit');
    }
    try {
      const currentLang = this.getCurrentLang();
      (window as any).switchLang?.(currentLang);
    } catch {}
  }

  private showErrorMessage(field: HTMLElement, messageKey: string): void {
    const errorDiv = field.parentElement?.querySelector('.error-message');
    if (errorDiv) {
      errorDiv.setAttribute('data-translate', messageKey);
      errorDiv.textContent = this.trLocal(messageKey);
      errorDiv.classList.remove('hidden');
      try {
        const currentLang = this.getCurrentLang();
        (window as any).switchLang?.(currentLang);
      } catch {}
    }
  }

  private removeErrorMessage(field: HTMLElement): void {
    const errorDiv = field.parentElement?.querySelector('.error-message');
    if (errorDiv) {
      errorDiv.classList.add('hidden');
    }
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    const toast = document.createElement('div');
    toast.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white transform transition-all duration-300 translate-y-0 opacity-0 z-50 ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    }`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.remove('translate-y-0', 'opacity-0');
    }, 100);

    setTimeout(() => {
      toast.classList.add('translate-y-2', 'opacity-0');
      setTimeout(() => {
        toast.remove();
      }, 300);
    }, 5000);
  }

  private showToastKey(key: string, type: 'success' | 'error'): void {
    const toast = document.createElement('div');
    toast.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white transform transition-all duration-300 translate-y-0 opacity-0 z-50 ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    }`;
    toast.setAttribute('data-translate', key);
    toast.textContent = this.trLocal(key);
    document.body.appendChild(toast);

    try {
      const currentLang = this.getCurrentLang();
      (window as any).switchLang?.(currentLang);
    } catch {}

    setTimeout(() => {
      toast.classList.remove('translate-y-0', 'opacity-0');
    }, 100);

    setTimeout(() => {
      toast.classList.add('translate-y-2', 'opacity-0');
      setTimeout(() => {
        toast.remove();
      }, 300);
    }, 5000);
  }
}

// Initialisation
document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('checklistForm')) {
    try {
      new ChecklistForm();
      console.log("✓ Formulaire de checklist initialisé avec succès");
    } catch (error) {
      console.error("❌ Erreur lors de l'initialisation du formulaire de checklist:", error);
    }
  }
});

export default ChecklistForm;
